// src/pages/OnboardingPage.jsx
// L'arrivée « Le Cabinet » — palier 10 de l'intégration React.
// Réf. visuelle EXACTE : design-mockups/concepts-v2/le-cabinet/onboarding.html
//
// IDÉE FORTE : on ne « remplit pas un formulaire » — on CONSTITUE son cabinet
// et on RENCONTRE son équipe. Parcours incarné, fil d'étapes vertical à gauche
// (pas une topnav), progression visible, Claire qui accueille de bout en bout :
//   1 · Votre cabinet  (nom, barreau)
//   2 · Vos domaines    → ça « active » les bons spécialistes (compteur, visages)
//   3 · Votre équipe     → les collaborateurs correspondants se présentent
//   4 · C'est prêt       (cérémonial → entrée dans Le Brief)
//
// SOCLE : on consomme les tokens `cab` (var(--cab-*)) + fonts Fraunces / Geist
// Mono (fonts.serifDisplay / fonts.monoCab) + les keyframes globales `cab*`
// (cabRing, cabBlink, cabRot, cabRise) définies dans index.css. Le traitement
// des fiches collaborateurs et la régie sombre de Claire sont alignés sur
// TeamPage (la « fiche vivante » / ClaireRegie). Vraies données : les 20
// collaborateurs de data/agents.js, domaines réels mappés aux spécialistes.
// La page peint sa propre surface — elle ne touche PAS aux variables « E ».
//
// ⚠️ NON-CASSANT : TOUTE la logique d'onboarding est préservée — les 4 étapes,
// la sélection des domaines (persistée localStorage `iavocat-onboarding-*`), la
// sauvegarde Supabase (`lawyers.firm_name` / `barreau`), le gating de
// progression (≥ 1 domaine pour avancer) et la redirection finale vers `/`.
// On réhabille le RENDU, pas les flux.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useIsMobile } from '../hooks/useIsMobile'
import { AGENTS } from '../data/agents'
import { cab as K, fonts as F } from '../theme'

// ─── Photo d'un collaborateur (données réelles AGENTS, fallback Supabase EU) ───
const photo = (id) =>
  AGENTS.find(a => a.id === id)?.photoUrl
  || `https://mhixfakpafenionfrpto.supabase.co/storage/v1/object/public/agent-photos/${id}.jpg`
const CLAIRE = photo('claire')
const agentById = (id) => AGENTS.find(a => a.id === id)

/* ════════════════════════════════════════════════════════════════════════
   DOMAINES réels → matière (token couleur) + spécialiste activé.
   `specialist` = id RÉEL du collaborateur principal mobilisé (data/agents.js).
   Aligné sur la maquette le-cabinet/onboarding.html.
   ════════════════════════════════════════════════════════════════════════ */
const DOMAINS = [
  { id: 'penal',        name: 'Pénal',                   matter: 'penal',       glyph: 'scale',      desc: 'Défense, instruction, comparutions.',     specialist: 'francoise' },
  { id: 'penal-int',    name: 'Pénal international',      matter: 'penal',       glyph: 'globe',      desc: 'Entraide, MAE, extradition.',             specialist: 'guillaume' },
  { id: 'corporel',     name: 'Préjudice corporel',      matter: 'corporel',    glyph: 'heartpulse', desc: 'Dommage corporel, resp. médicale.',       specialist: 'louise' },
  { id: 'famille',      name: 'Famille',                 matter: 'famille',     glyph: 'people',     desc: 'Divorce, JAF, DIP familial.',             specialist: 'margaux' },
  { id: 'successions',  name: 'Successions',             matter: 'famille',     glyph: 'scroll',     desc: 'Liquidation, partage, réserve.',          specialist: 'pierre' },
  { id: 'affaires',     name: 'Contrats & Sociétés',     matter: 'affaires',    glyph: 'briefcase',  desc: 'Contrats, sociétés, recouvrement.',       specialist: 'thomas' },
  { id: 'pi',           name: 'PI & Consommation',       matter: 'pi',          glyph: 'bulb',       desc: 'Marques, contrefaçon, conso.',            specialist: 'camille' },
  { id: 'immobilier',   name: 'Immobilier',              matter: 'immobilier',  glyph: 'building',   desc: 'Baux, copropriété, voisinage.',           specialist: 'nadia' },
  { id: 'travail',      name: 'Travail & Sécu. sociale', matter: 'travail',     glyph: 'handshake',  desc: 'Licenciement, CPH, accident.',            specialist: 'antoine' },
  { id: 'administratif',name: 'Administratif',           matter: 'admin',       glyph: 'gavel',      desc: 'Excès de pouvoir, référés, urbanisme.',   specialist: 'helene' },
  { id: 'environnement',name: 'Environnement & énergie', matter: 'env',         glyph: 'leaf',       desc: 'ICPE, énergie, étude d\'impact.',         specialist: 'sarah' },
  { id: 'procedures-collectives', name: 'Procédures collectives', matter: 'proc', glyph: 'recover',  desc: 'Sauvegarde, redressement, créances.',     specialist: 'mathilde' },
  { id: 'fiscal',       name: 'Fiscal',                  matter: 'fiscal',      glyph: 'calc',       desc: 'Contrôle, contentieux, patrimoine.',      specialist: 'alexandre' },
  { id: 'rgpd',         name: 'RGPD & données',          matter: 'rgpd',        glyph: 'shieldlock', desc: 'Conformité, registre, CNIL.',             specialist: 'lea' },
]

// Présents d'office (pilotage & secrétariat) — montrés à l'étape « équipe »,
// non sélectionnables, ids RÉELS.
const ALWAYS = ['claire', 'sophie', 'emilie']

// Matière (token couleur) par collaborateur — aligné sur TeamPage.AGENT_MATTER.
const AGENT_MATTER = {
  claire: 'bordeaux', sophie: 'secretariat', emilie: 'secretariat', nicolas: 'bordeaux',
  francoise: 'penal', guillaume: 'penal',
  louise: 'corporel', margaux: 'famille', pierre: 'famille',
  thomas: 'affaires', camille: 'pi', nadia: 'immobilier', alexandre: 'fiscal',
  antoine: 'travail',
  helene: 'admin', sarah: 'env',
  mathilde: 'proc', lea: 'rgpd', marc: 'affaires', julien: 'secretariat',
}

// Libellé court de spécialité (« domaine · sous-domaine ») — aligné sur
// TeamPage.AGENT_SPEC. Statique, dérivé du rôle réel — pas d'activité inventée.
const AGENT_SPEC = {
  claire: 'Civiliste · orchestratrice du cabinet',
  sophie: 'Secrétaire juridique',
  emilie: 'Accueil & orientation',
  francoise: 'Pénaliste senior',
  guillaume: 'Pénal international',
  louise: 'Préjudice corporel & responsabilité médicale',
  margaux: 'Famille & DIP familial',
  pierre: 'Successions',
  thomas: 'Contrats & Sociétés',
  camille: 'PI & Consommation',
  nadia: 'Immobilier',
  alexandre: 'Fiscal',
  antoine: 'Travail & sécurité sociale',
  helene: 'Administratif',
  sarah: 'Environnement, énergie & ICPE',
  mathilde: 'Procédures collectives',
  lea: 'RGPD & données personnelles',
}

// « Ce qu'il peut faire pour vous » — capacités statiques (≈ reformulation de la
// bio réelle, adossées aux skills). Aligné sur TeamPage.AGENT_CAN. Pas d'activité
// live ; curaté pour rester lisible (les slugs techniques ne le seraient pas).
const AGENT_CAN = {
  claire: ['Répartir un dossier', 'Qualifier un litige', "Coordonner l'équipe", "Faire le point sur l'agenda"],
  sophie: ['Rédiger un courrier', 'Trier la boîte mail', 'Préparer des réponses', 'Mettre en forme un acte'],
  emilie: ['Orienter une demande', 'Répondre à une question rapide', 'Aider à formuler', 'Prendre un message'],
  francoise: ['Rédiger des conclusions', 'Détecter une nullité', 'Synthèse de défense', 'Aménagement de peine'],
  guillaume: ['Entraide pénale', "Mandat d'arrêt européen", 'Extradition', 'Traduire un acte étranger'],
  louise: ['Chiffrer un préjudice', 'Lire une expertise médicale', 'Poste par poste (Dintilhac)', 'Saisir la CIVI'],
  margaux: ['Rédiger une requête JAF', 'Résidence des enfants', 'Divorce & pension', 'DIP familial'],
  pierre: ['Liquider une succession', 'Partage & indivision', 'Réserve héréditaire', 'Recel successoral'],
  thomas: ['Rédiger une mise en demeure', 'Rédiger un contrat', "Pacte d'associés", 'Cession de parts'],
  camille: ['Protéger une marque', 'Action en contrefaçon', 'Clauses abusives', 'Droit de la consommation'],
  nadia: ['Rédiger un bail', 'Recouvrer un loyer', 'Copropriété', 'Vices cachés'],
  alexandre: ['Répondre à un contrôle fiscal', 'Contentieux fiscal', 'Réclamation', 'Plus-values / IFI'],
  antoine: ['Saisir le CPH', 'Contester un licenciement', 'Accident du travail', 'Faute inexcusable'],
  helene: ['Recours pour excès de pouvoir', 'Référé administratif', 'Droit des étrangers / OQTF', "Contentieux de l'asile"],
  sarah: ['Autorisation ICPE', 'Contentieux environnemental', "Droit de l'énergie", "Étude d'impact"],
  mathilde: ['Déclarer une créance', 'Sauvegarde / redressement', 'Liquidation judiciaire', 'Relevé de forclusion'],
  lea: ['Mise en conformité RGPD', 'Registre des traitements', 'Répondre à la CNIL', 'AIPD'],
}

// Mot d'accueil (1re personne) — réutilise la bio RÉELLE (data/agents.js),
// raccourcie à la 1re phrase pour la « présentation ». Pas de texte inventé.
function helloFor(id) {
  const bio = agentById(id)?.bio || ''
  // 1re phrase de la bio (jusqu'au premier « . » ou « — »), bornée.
  const first = bio.split(/(?<=[.])\s|—/)[0]?.trim() || bio.trim()
  return first.length > 150 ? first.slice(0, 147).trimEnd() + '…' : first
}

const monogram = (name) => (name || '?').trim().charAt(0).toUpperCase()

// Liste dédupliquée des spécialistes mobilisés par les domaines choisis.
function activatedSpecialists(selected) {
  const seen = {}, out = []
  selected.forEach((id) => {
    const d = DOMAINS.find(x => x.id === id)
    if (d && !seen[d.specialist]) { seen[d.specialist] = 1; out.push(d.specialist) }
  })
  return out
}

// Token couleur d'une matière → { color, soft }. Gère les matières sans `-soft`
// dédié (affaires/proc/fiscal/env/bordeaux) — aligné sur TeamPage.matterTone.
const SOFT_FALLBACK = {
  affaires: 'var(--cab-civil-soft)',
  proc: 'var(--cab-secretariat-soft)',
  fiscal: 'var(--cab-pi-soft)',
  env: 'var(--cab-immobilier-soft)',
  bordeaux: 'var(--cab-bordeaux-tint)',
}
function matterTone(matter) {
  const m = matter || 'admin'
  return { color: `var(--cab-${m})`, soft: SOFT_FALLBACK[m] || `var(--cab-${m}-soft)` }
}

/* ─── Icônes (stroke currentColor) — repris de la maquette / TeamPage ─── */
const Ic = {
  shieldLine: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.6" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  arrow: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  back: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  chevron: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  caretDown: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  check: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  home: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 21V8l9-5 9 5v13M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>),
  grid: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="4" y="4" width="6.5" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.7" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.7" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.7" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.7" /></svg>),
  people: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" /><circle cx="17" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.6" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M15 19c0-2 1-3.4 2.8-3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
}
// Glyphes de matière pour les cartes domaine (stroke currentColor).
const GLYPHS = {
  scale: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 3v18M7 21h10M12 5l-6 2 2.5 5a2.6 2.6 0 01-5 0L6 7M12 5l6 2-2.5 5a2.6 2.6 0 005 0L18 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  globe: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" /><path d="M3 12h18M12 3c2.5 2.4 2.5 15.6 0 18M12 3c-2.5 2.4-2.5 15.6 0 18" stroke="currentColor" strokeWidth="1.5" /></svg>),
  heartpulse: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 20S4 14.5 4 9a4.2 4.2 0 017.5-2.6A4.2 4.2 0 0119 9c0 1.2-.4 2.3-1 3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><path d="M11 12h2l1.2-2 1.6 4 1-2H21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  people: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" /><circle cx="17" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.6" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M15 19c0-2 1-3.4 2.8-3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  scroll: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M6 4h11a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M9 9h6M9 13h6M9 17h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
  briefcase: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3" y="7" width="18" height="13" rx="2.4" stroke="currentColor" strokeWidth="1.7" /><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M3 13h18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  bulb: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M9.8 18h4.4M10 21h4M12 2a6 6 0 00-4 10.5c.7.7 1 1.2 1 2.5h6c0-1.3.3-1.8 1-2.5A6 6 0 0012 2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  building: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="5" y="3" width="14" height="18" rx="1.6" stroke="currentColor" strokeWidth="1.6" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
  handshake: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 11l4-4 4 2 3-1 6 5M7 7l-4 4 3 3M21 13l-3 3-3-2-2 2-2-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  gavel: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M14 3l7 7-3 3-7-7zM4 20l7-7M2 22h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  leaf: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 19c0-8 6-13 14-14 0 8-5 14-13 14H5zM7 17C12 13 14 9 16 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  recover: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 12a9 9 0 109-9 9 9 0 00-7 3.3M3 4v3.3H6.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  shieldlock: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.6" /><rect x="9.2" y="11" width="5.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.4" /><path d="M10.4 11V9.8a1.6 1.6 0 013.2 0V11" stroke="currentColor" strokeWidth="1.4" /></svg>),
  calc: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M8 7h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15v3M8 18h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
}

const STEPS = [
  { t: 'Votre cabinet', d: 'Le nom, le barreau' },
  { t: 'Vos domaines',  d: 'Les matières que vous traitez' },
  { t: 'Votre équipe',  d: 'Vos spécialistes se présentent' },
  { t: "C'est prêt",    d: 'Votre cabinet ouvre' },
]

const BARREAUX = ['Paris', 'Hauts-de-Seine', 'Lyon', 'Marseille', 'Bordeaux', 'Lille', 'Toulouse', 'Nantes', 'Strasbourg', 'Montpellier', 'Rennes', 'Nice', 'Versailles', 'Aix-en-Provence', 'Autre barreau']
const EFFECTIFS = ['Avocat individuel', '2 à 5 avocats', '6 à 20 avocats', 'Plus de 20 avocats']

/* ════════════════════════════════════════════════════════════════════════
   Bandeau Claire — hôte du parcours (régie sombre signature, alignée TeamPage)
   ════════════════════════════════════════════════════════════════════════ */
function ClaireHost({ kicker, children, isMobile }) {
  return (
    <div style={{
      position: 'relative', borderRadius: K.rLg, overflow: 'hidden', marginBottom: isMobile ? 20 : 26,
      background: 'radial-gradient(125% 130% at 6% 0%, rgba(168,50,74,.40), transparent 56%), radial-gradient(90% 120% at 100% 100%, rgba(46,114,184,.12), transparent 60%), linear-gradient(160deg, #3a1620, #271017)',
      boxShadow: '0 26px 60px -34px rgba(78,22,32,.85), var(--cab-lift-2)', color: '#F6ECEE',
    }}>
      <div style={{ padding: isMobile ? '18px 16px' : '22px 26px', display: 'flex', gap: isMobile ? 14 : 18, alignItems: 'center' }}>
        <span style={{ position: 'relative', width: isMobile ? 50 : 60, height: isMobile ? 50 : 60, flex: 'none' }}>
          <span aria-hidden="true" style={{ position: 'absolute', inset: -5, borderRadius: 21, border: '1.5px solid rgba(255,180,195,.7)', opacity: .6, animation: 'cabRing 3.4s var(--cab-ease-soft) infinite' }} />
          <img src={CLAIRE} alt="Claire, orchestratrice du cabinet" loading="lazy"
            style={{ width: '100%', height: '100%', borderRadius: 17, objectFit: 'cover', objectPosition: 'center top', border: '1.5px solid rgba(255,255,255,.28)', boxShadow: '0 10px 26px rgba(0,0,0,.5)' }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(255,190,202,.95)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 9 }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#5BE0A6', boxShadow: '0 0 0 3px rgba(91,224,166,.22)', animation: 'cabBlink 2.6s infinite' }} />
            {kicker}
          </div>
          <div style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: isMobile ? 16 : 'clamp(16px, 2vw, 20px)', lineHeight: 1.32, letterSpacing: '-.01em', color: '#fff', maxWidth: '54ch' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

/* En-tête de panneau (kicker · étape, titre serif, sous-titre) */
function PanelHead({ stepNo, title, sub }) {
  return (
    <div style={{ marginBottom: 26, maxWidth: '60ch' }}>
      <div style={{ fontFamily: F.monoCab, fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: K.bordeaux, display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 13 }}>
        Étape {stepNo} <span style={{ fontFamily: F.monoCab, fontSize: 10, color: K.ink3 }}>· sur 4</span>
      </div>
      <h2 style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: 'clamp(26px, 3.4vw, 40px)', lineHeight: 1.1, letterSpacing: '-.02em', color: K.ink, margin: 0 }}>{title}</h2>
      <p style={{ fontSize: 15, color: K.ink2, marginTop: 13, lineHeight: 1.55, maxWidth: '54ch' }}>{sub}</p>
    </div>
  )
}

/* Champ de formulaire (label + input ivoire) */
function Field({ label, req, hint, children, wide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...(wide && { gridColumn: '1 / -1' }) }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: K.ink2, display: 'flex', alignItems: 'center', gap: 8 }}>
        {label}
        {req && <span aria-hidden="true" style={{ color: K.bordeaux, fontWeight: 700 }}>*</span>}
        {hint && <span style={{ fontSize: 11.5, color: K.ink4, fontWeight: 400 }}>{hint}</span>}
      </label>
      {children}
    </div>
  )
}

const inputBase = {
  width: '100%', borderRadius: K.rMd, background: 'var(--cab-card)', border: `1px solid ${K.hair2}`,
  padding: '13px 15px', fontSize: 14.5, color: K.ink, boxShadow: K.lift1,
  fontFamily: F.sans, outline: 'none', transition: 'border-color .2s, box-shadow .2s',
}
function TextInput({ value, onChange, ...rest }) {
  return (
    <input value={value} onChange={onChange} style={inputBase}
      onFocus={e => { e.target.style.borderColor = 'var(--cab-bordeaux-hi)'; e.target.style.boxShadow = `0 0 0 3px var(--cab-bordeaux-tint-2), ${K.lift1}` }}
      onBlur={e => { e.target.style.borderColor = K.hair2; e.target.style.boxShadow = K.lift1 }}
      {...rest} />
  )
}
function SelectInput({ value, onChange, placeholder, options }) {
  return (
    <div style={{ position: 'relative' }}>
      <select value={value} onChange={onChange}
        style={{ ...inputBase, appearance: 'none', WebkitAppearance: 'none', paddingRight: 40, cursor: 'pointer' }}
        onFocus={e => { e.target.style.borderColor = 'var(--cab-bordeaux-hi)'; e.target.style.boxShadow = `0 0 0 3px var(--cab-bordeaux-tint-2), ${K.lift1}` }}
        onBlur={e => { e.target.style.borderColor = K.hair2; e.target.style.boxShadow = K.lift1 }}>
        <option value="" disabled>{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <Ic.caretDown style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: K.ink3, pointerEvents: 'none' }} />
    </div>
  )
}

/* ════════════════════════════════════════════════════════════════════════
   Carte « domaine » sélectionnable (étape 2)
   ════════════════════════════════════════════════════════════════════════ */
function DomainCard({ domain, selected, onToggle, isMobile }) {
  const [hovered, setHovered] = useState(false)
  const tone = matterTone(domain.matter)
  const Glyph = GLYPHS[domain.glyph] || GLYPHS.briefcase
  const who = agentById(domain.specialist)
  const [imgError, setImgError] = useState(false)
  return (
    <button
      type="button" role="checkbox" aria-checked={selected} onClick={() => onToggle(domain.id)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', textAlign: 'left', borderRadius: K.rMd, overflow: 'hidden', background: K.card,
        border: `1px solid ${selected ? tone.color : K.hair2}`,
        boxShadow: selected ? `0 0 0 1px ${tone.color}, ${K.lift2}` : (hovered ? K.lift2 : K.lift1),
        padding: '16px 16px 15px', cursor: 'pointer',
        transform: hovered && !selected ? 'translateY(-3px)' : 'none',
        transition: 'transform .25s var(--cab-ease), box-shadow .25s var(--cab-ease), border-color .2s',
        display: 'flex', flexDirection: 'column', gap: 11, minHeight: isMobile ? 0 : 128,
      }}
    >
      {/* filet de matière à gauche */}
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: tone.color, opacity: selected ? 1 : .55 }} />
      {/* lavis */}
      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, opacity: selected ? .7 : (hovered ? .4 : 0), transition: 'opacity .3s', pointerEvents: 'none', background: `radial-gradient(90% 130% at 92% 0%, ${tone.soft}, transparent 72%)` }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 10, flex: 'none', display: 'grid', placeItems: 'center', color: selected ? '#fff' : tone.color, background: selected ? tone.color : tone.soft, transition: '.25s' }}>
          <Glyph style={{ width: 18, height: 18 }} />
        </span>
        <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 7, flex: 'none', display: 'grid', placeItems: 'center', color: '#fff', border: `2px solid ${selected ? tone.color : K.hair3}`, background: selected ? tone.color : 'transparent', transition: '.22s var(--cab-ease)' }}>
          <Ic.check style={{ width: 13, height: 13, opacity: selected ? 1 : 0, transform: selected ? 'scale(1)' : 'scale(.5)', transition: '.22s var(--cab-ease)' }} />
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: K.ink, letterSpacing: '-.005em', lineHeight: 1.2 }}>{domain.name}</div>
      {!isMobile && <div style={{ fontSize: 11.5, color: K.ink3, lineHeight: 1.4, marginTop: -3 }}>{domain.desc}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', fontSize: 11, color: K.ink3 }}>
        {who?.photoUrl && !imgError && (
          <img src={photo(domain.specialist)} alt="" loading="lazy" onError={() => setImgError(true)}
            style={{ width: 22, height: 22, borderRadius: 7, objectFit: 'cover', objectPosition: 'center top', border: `1px solid ${K.hair2}` }} />
        )}
        <span><b style={{ color: K.ink2, fontWeight: 600 }}>{who?.name || domain.name}</b> rejoint l'équipe</span>
      </div>
    </button>
  )
}

/* ════════════════════════════════════════════════════════════════════════
   Fiche collaborateur « se présente » (étape 3) — fidèle à TeamPage.SpecCard,
   variante « mot d'accueil » (bio réelle) + capacités. Vraies données.
   ════════════════════════════════════════════════════════════════════════ */
function SpecCard({ agent, present, index }) {
  const [hovered, setHovered] = useState(false)
  const [imgError, setImgError] = useState(false)
  const matter = AGENT_MATTER[agent.id] || agent.category || 'admin'
  const tone = matterTone(matter)
  const spec = AGENT_SPEC[agent.id] || agent.title
  const can = AGENT_CAN[agent.id] || []
  const isLead = agent.id === 'claire'
  const hello = helloFor(agent.id)

  return (
    <article
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', textAlign: 'left', borderRadius: K.rLg, overflow: 'hidden', background: K.card,
        boxShadow: isLead ? '0 2px 5px rgba(40,28,32,.06), 0 18px 40px -20px rgba(122,31,46,.45)' : K.lift2,
        transition: 'transform .35s var(--cab-ease), box-shadow .35s var(--cab-ease)',
        transform: hovered ? 'translateY(-5px)' : 'none',
        display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0,
        opacity: 0, animation: 'cabRise .5s var(--cab-ease) forwards', animationDelay: `${index * 70}ms`,
        ...(hovered && { boxShadow: K.lift3 }),
      }}
    >
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 4, zIndex: 2, background: tone.color }} />
      <span aria-hidden="true" style={{ position: 'absolute', inset: '0 0 auto', height: 120, opacity: .5, pointerEvents: 'none', background: `radial-gradient(90% 130% at 88% 0%, ${tone.soft}, transparent 72%)` }} />

      {isLead && (
        <span aria-hidden="true" style={{
          position: 'absolute', top: 14, right: 14, zIndex: 3, fontFamily: F.monoCab, fontSize: 9,
          letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 600, color: '#fff',
          background: 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))',
          padding: '4px 10px', borderRadius: 99, boxShadow: '0 4px 12px -4px var(--cab-bordeaux-glow)',
        }}>Orchestratrice</span>
      )}

      {/* En-tête : visage + identité */}
      <div style={{ position: 'relative', padding: '20px 20px 0', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <span style={{ position: 'relative', width: 58, height: 58, flex: 'none' }}>
          {agent.photoUrl && !imgError ? (
            <img src={agent.photoUrl} alt={agent.name} loading="lazy" onError={() => setImgError(true)}
              style={{ width: '100%', height: '100%', borderRadius: 16, objectFit: 'cover', objectPosition: 'center top', border: `1px solid ${K.hair2}`, background: K.paper2 }} />
          ) : (
            <span aria-hidden="true" style={{ width: '100%', height: '100%', borderRadius: 16, display: 'grid', placeItems: 'center', fontFamily: F.serifDisplay, fontWeight: 600, fontSize: 22, color: '#fff', background: `linear-gradient(150deg, ${tone.color}, color-mix(in srgb, ${tone.color} 70%, #000))` }}>{monogram(agent.name)}</span>
          )}
          <span aria-hidden="true" style={{ position: 'absolute', left: 2, right: 2, bottom: -3, height: 3, borderRadius: 99, background: tone.color }} />
          <span aria-hidden="true" title="Disponible" style={{ position: 'absolute', right: -3, bottom: -3, width: 16, height: 16, borderRadius: '50%', border: '2.5px solid var(--cab-card)', background: K.verified }} />
        </span>
        <div style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
          <div style={{ fontFamily: F.serifDisplay, fontWeight: 600, fontSize: 18.5, letterSpacing: '-.01em', color: K.ink, lineHeight: 1.1 }}>{agent.name}</div>
          <span style={{ marginTop: 6, fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 10px', borderRadius: 99, color: tone.color, background: tone.soft }}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
            {spec.split('·')[0].trim()}
          </span>
        </div>
      </div>

      {/* Rôle complet */}
      <div style={{ padding: '13px 20px 0', fontSize: 13, color: K.ink2, lineHeight: 1.5 }}>{spec}</div>

      {/* Mot d'accueil (la présentation) — bio réelle */}
      <div style={{ margin: '13px 20px 0', borderRadius: 13, padding: '12px 14px', border: `1px solid ${K.hair}`, background: K.cardSoft, position: 'relative' }}>
        <span aria-hidden="true" style={{ position: 'absolute', left: 14, top: -6, width: 11, height: 11, background: K.cardSoft, borderLeft: `1px solid ${K.hair}`, borderTop: `1px solid ${K.hair}`, transform: 'rotate(45deg)' }} />
        <div style={{ fontFamily: F.monoCab, fontSize: 9.5, letterSpacing: '.14em', textTransform: 'uppercase', color: K.ink4, marginBottom: 6 }}>
          {present ? "Présent d'office" : 'Nouveau dans l’équipe'}
        </div>
        <p style={{ fontFamily: F.serifDisplay, fontStyle: 'italic', fontSize: 13.5, color: K.ink, lineHeight: 1.5, margin: 0 }}>« {hello} »</p>
      </div>

      {/* Ce qu'il peut faire pour vous */}
      {can.length > 0 && (
        <div style={{ padding: '13px 20px 0' }}>
          <div style={{ fontFamily: F.monoCab, fontSize: 9.5, letterSpacing: '.16em', textTransform: 'uppercase', color: K.ink4, marginBottom: 9 }}>Ce qu'il peut faire pour vous</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {can.map(t => (
              <span key={t} style={{ fontSize: 11.5, color: K.ink2, background: K.paper, border: `1px solid ${K.hair}`, padding: '5px 10px', borderRadius: 8, lineHeight: 1.2 }}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Pied : statut « a rejoint » */}
      <div style={{ marginTop: 'auto', padding: '15px 20px 18px' }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: K.verified, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Ic.check style={{ width: 14, height: 14 }} />
          {present ? 'Pilote vos dossiers' : 'A rejoint votre cabinet'}
        </span>
      </div>
    </article>
  )
}

/* Pile de visages (compteur d'équipe activée) */
function FaceStack({ ids, size = 24 }) {
  return (
    <span style={{ display: 'flex' }} aria-hidden="true">
      {ids.slice(0, 6).map((id, i) => (
        <img key={id} src={photo(id)} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none' }}
          style={{ width: size, height: size, borderRadius: 7, objectFit: 'cover', objectPosition: 'center top', border: `2px solid ${K.paper}`, marginLeft: i === 0 ? 0 : -7, boxShadow: K.lift1 }} />
      ))}
    </span>
  )
}

/* ════════════════════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════════════════════ */
export default function OnboardingPage() {
  const navigate = useNavigate()
  const { lawyer } = useAuth()
  const isMobile = useIsMobile()

  // ── État du parcours — 0..3 (logique préservée : 4 étapes) ──
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)

  // Étape 1 — Cabinet (firm_name + barreau sauvegardés en base ; autres champs UI)
  const [cabinetName, setCabinetName] = useState(lawyer?.firm_name || '')
  const [avocat, setAvocat] = useState(lawyer?.name || '')
  const [barreau, setBarreau] = useState(lawyer?.barreau || '')
  const [toque, setToque] = useState('')
  const [effectif, setEffectif] = useState('')

  // Étape 2 — Domaines (persistés localStorage — clé conservée à l'identique)
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem('iavocat-onboarding-specialties') || '[]') }
    catch { return [] }
  })
  const toggleDom = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id])
  }

  // ── Persistance — contrat préservé : localStorage + update Supabase ──
  const persist = async () => {
    localStorage.setItem('iavocat-onboarding-specialties', JSON.stringify(selected))
    localStorage.setItem('iavocat-onboarding-completed', '1')
    if (lawyer?.id) {
      try {
        await supabase.from('lawyers').update({
          firm_name: cabinetName || null,
          barreau:   barreau || null,
        }).eq('id', lawyer.id)
      } catch { /* silent — l'onboarding ne doit pas bloquer */ }
    }
  }

  // ── Gating de progression (préservé) ──
  const cabinetValid = !!(cabinetName.trim() && avocat.trim() && barreau)
  const canGoNext = step === 0 ? cabinetValid : step === 1 ? selected.length > 0 : true

  const goTo = (target) => {
    if (target < 0 || target > STEPS.length - 1) return
    if (target >= 1 && !cabinetValid) return       // garde-fou étape 1
    if (target >= 2 && selected.length === 0) { setStep(1); return } // garde-fou étape 2
    setStep(target)
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' })
  }
  const onBack = () => { if (step === 0) { navigate('/'); return } goTo(step - 1) }
  const onSkip = async () => { await persist(); navigate('/') }
  const onEnter = async () => { setSaving(true); await persist(); setSaving(false); navigate('/') }

  // Étape suivante (avance d'un cran) — sauf dernière étape (cérémonial → entrée)
  const onNext = () => { if (step < STEPS.length - 1) goTo(step + 1) }

  // ── Dérivés ──
  const specIds = activatedSpecialists(selected)
  const rosterIds = ALWAYS.concat(specIds.filter(w => !ALWAYS.includes(w)))
  const newCount = rosterIds.length - ALWAYS.length

  return (
    <div style={{
      position: 'relative', minHeight: '100vh', fontFamily: F.sans, background: K.paper, color: K.ink,
      display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '332px 1fr', maxWidth: 1500, margin: '0 auto',
      width: '100%', overflowX: 'hidden',
    }}>
      {/* ════════ Ambiance de fond ════════ */}
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 680, height: 680, left: -160, top: -240, opacity: .55, borderRadius: '50%', filter: 'blur(110px)', background: 'radial-gradient(circle, rgba(168,50,74,.20), transparent 66%)' }} />
        <div style={{ position: 'absolute', width: 620, height: 620, right: -180, bottom: -220, opacity: .5, borderRadius: '50%', filter: 'blur(110px)', background: 'radial-gradient(circle, rgba(46,114,184,.14), transparent 70%)' }} />
      </div>

      {/* ════════════════ FIL D'ÉTAPES (rail gauche, pas une topnav) ════════════════ */}
      <aside style={{
        position: isMobile ? 'static' : 'sticky', top: 0, alignSelf: 'start', height: isMobile ? 'auto' : '100vh',
        display: 'flex', flexDirection: 'column', zIndex: 2, minWidth: 0,
        padding: isMobile ? '22px 20px' : '34px 30px 30px',
        borderRight: isMobile ? 'none' : `1px solid ${K.hair}`,
        borderBottom: isMobile ? `1px solid ${K.hair}` : 'none',
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--cab-card) 50%, transparent), color-mix(in srgb, var(--cab-card-soft) 20%, transparent))',
      }}>
        {/* Brand + « Plus tard » */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: isMobile ? 22 : 38 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span aria-hidden="true" style={{ width: 38, height: 38, borderRadius: 12, flex: 'none', position: 'relative', background: 'linear-gradient(150deg, var(--cab-bordeaux-hi), var(--cab-bordeaux-deep))', boxShadow: '0 6px 16px -4px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.3)', display: 'grid', placeItems: 'center' }}>
              <svg viewBox="0 0 24 24" fill="none" style={{ width: 21, height: 21 }}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" fill="#fff" fillOpacity=".95" /><path d="M9 12l2 2 4-4.5" stroke="var(--cab-bordeaux)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <span style={{ fontFamily: F.serifDisplay, fontWeight: 500, fontSize: 21, letterSpacing: '-.01em', color: K.ink }}>IAvocat<b style={{ color: K.bordeaux, fontWeight: 600 }}>.ai</b></span>
          </span>
          <button type="button" onClick={onSkip}
            style={{ fontSize: 12, fontWeight: 600, color: K.ink3, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 99, background: 'none', border: 'none', cursor: 'pointer', transition: '.2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = K.ink; e.currentTarget.style.background = K.card; e.currentTarget.style.boxShadow = K.lift1 }}
            onMouseLeave={e => { e.currentTarget.style.color = K.ink3; e.currentTarget.style.background = 'none'; e.currentTarget.style.boxShadow = 'none' }}>
            Plus tard<Ic.chevron style={{ width: 13, height: 13 }} />
          </button>
        </div>

        {/* Intro du rail */}
        {!isMobile && (
          <div style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: '.2em', textTransform: 'uppercase', color: K.ink3, display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
              <span aria-hidden="true" style={{ width: 22, height: 1, background: K.bordeaux }} />
              Mise en route · 4 étapes
            </div>
            <h1 style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: 27, lineHeight: 1.12, letterSpacing: '-.02em', color: K.ink, margin: 0 }}>
              Constituez<br />votre <b style={{ fontWeight: 600, color: K.bordeaux }}>cabinet</b>.
            </h1>
          </div>
        )}

        {/* La timeline verticale (desktop) / horizontale compacte (mobile) */}
        <nav aria-label="Étapes de mise en route" style={{
          position: 'relative', flex: isMobile ? 'none' : 1,
          paddingLeft: isMobile ? 0 : 6,
          display: isMobile ? 'flex' : 'block', gap: isMobile ? 6 : 0,
          overflowX: isMobile ? 'auto' : 'visible', minWidth: 0, width: isMobile ? '100%' : undefined,
        }}>
          {/* rail + progression (desktop seulement) */}
          {!isMobile && (
            <>
              <span aria-hidden="true" style={{ position: 'absolute', left: 21, top: 20, bottom: 20, width: 2, borderRadius: 2, background: 'linear-gradient(180deg, var(--cab-hair-3), var(--cab-hair-2))' }} />
              <span aria-hidden="true" style={{ position: 'absolute', left: 21, top: 20, width: 2, borderRadius: 2, height: `${step / (STEPS.length - 1) * 100}%`, maxHeight: 'calc(100% - 40px)', background: 'linear-gradient(180deg, var(--cab-bordeaux), var(--cab-bordeaux-hi))', transition: 'height .6s var(--cab-ease)', zIndex: 1 }} />
            </>
          )}
          {STEPS.map((s, i) => {
            const done = i < step
            const current = i === step
            const clickable = i <= step || i === step + 1
            return (
              <button
                key={s.t} type="button"
                aria-current={current ? 'step' : undefined}
                disabled={!clickable}
                onClick={() => { if (i <= step) goTo(i); else if (i === step + 1) goTo(i) }}
                style={{
                  position: 'relative', display: 'flex', gap: isMobile ? 9 : 16,
                  alignItems: isMobile ? 'center' : 'flex-start',
                  flexDirection: isMobile ? 'column' : 'row',
                  textAlign: isMobile ? 'center' : 'left',
                  padding: isMobile ? '2px 4px' : '0 0 26px', width: isMobile ? undefined : '100%',
                  minWidth: isMobile ? 108 : undefined, flex: isMobile ? '1 1 0' : undefined,
                  background: 'none', border: 'none', cursor: clickable ? 'pointer' : 'default',
                  opacity: 1,
                }}
              >
                {/* knob */}
                <span aria-hidden="true" style={{
                  position: 'relative', zIndex: 2, flex: 'none', width: 32, height: 32, borderRadius: 11, display: 'grid', placeItems: 'center',
                  fontFamily: F.monoCab, fontSize: 12, fontWeight: 600, transition: '.35s var(--cab-ease)',
                  ...(current ? {
                    background: 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))', color: '#fff', border: '1px solid transparent',
                    boxShadow: '0 8px 18px -7px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.3)', transform: 'scale(1.04)',
                  } : done ? {
                    background: 'var(--cab-secretariat-soft)', border: '1px solid rgba(21,135,95,.3)', color: K.verified,
                  } : {
                    background: K.card, border: `1px solid ${K.hair2}`, color: K.ink3, boxShadow: K.lift1,
                  }),
                }}>
                  {done ? <Ic.check style={{ width: 15, height: 15 }} /> : i + 1}
                </span>
                {/* libellé */}
                <span style={{ paddingTop: isMobile ? 0 : 5, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: isMobile ? 12 : 14, fontWeight: 600, letterSpacing: '-.005em', color: current ? K.ink : done ? K.ink2 : K.ink2, transition: 'color .25s' }}>{s.t}</span>
                  {!isMobile && <span style={{ display: 'block', fontSize: 12, color: current ? K.ink3 : K.ink4, marginTop: 3, lineHeight: 1.4, transition: 'color .25s' }}>{s.d}</span>}
                </span>
              </button>
            )
          })}
        </nav>

        {/* Pied de rail — confidentialité (desktop) */}
        {!isMobile && (
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${K.hair}`, fontSize: 11.5, color: K.ink3, display: 'flex', alignItems: 'flex-start', gap: 9, lineHeight: 1.5 }}>
            <Ic.shieldLine style={{ width: 15, height: 15, color: K.verified, flex: 'none', marginTop: 1 }} />
            <span><b style={{ color: K.ink2, fontWeight: 600 }}>Données hébergées en Union européenne.</b> Vos informations sont couvertes par le secret professionnel.</span>
          </div>
        )}
      </aside>

      {/* ════════════════ LA SCÈNE (panneaux d'étape) ════════════════ */}
      <main style={{ position: 'relative', zIndex: 2, padding: isMobile ? '24px 16px 32px' : '42px 56px 56px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* ───────── ÉTAPE 1 — Votre cabinet ───────── */}
        {step === 0 && (
          <section key="p0" role="group" aria-label="Étape 1 — Votre cabinet" style={{ display: 'flex', flexDirection: 'column', flex: 1, animation: 'cabRise .5s var(--cab-ease) both' }}>
            <ClaireHost kicker="Claire · Orchestratrice — votre accueil" isMobile={isMobile}>
              Bienvenue, Maître. Je suis Claire, je coordonne le cabinet. <em style={{ fontStyle: 'italic', color: K.gildHi }}>Présentons-nous</em> — quelques mots sur votre cabinet et je prépare votre espace.
            </ClaireHost>

            <PanelHead stepNo="1"
              title={<>Votre cabinet, <b style={{ fontWeight: 600, color: K.bordeaux }}>pour commencer</b>.</>}
              sub={<>Ces informations personnalisent vos actes (en-têtes, signatures) et vos échanges avec les juridictions. <b style={{ color: K.ink, fontWeight: 600 }}>Rien n'est définitif</b> — vous ajusterez tout depuis vos paramètres.</>} />

            <div style={{ borderRadius: K.rLg, background: 'linear-gradient(180deg, var(--cab-card), var(--cab-card-soft))', boxShadow: K.lift2, padding: isMobile ? '20px 18px' : '28px 30px', maxWidth: 680 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '18px 20px' }}>
                <Field label="Nom du cabinet" req wide>
                  <TextInput value={cabinetName} onChange={e => setCabinetName(e.target.value)} placeholder="ex. Cabinet Lefèvre & Associés" autoComplete="organization" />
                </Field>
                <Field label="Avocat référent" req>
                  <TextInput value={avocat} onChange={e => setAvocat(e.target.value)} placeholder="ex. Me Camille Lefèvre" autoComplete="name" />
                </Field>
                <Field label="Barreau" req>
                  <SelectInput value={barreau} onChange={e => setBarreau(e.target.value)} placeholder="Sélectionnez votre barreau…" options={BARREAUX} />
                </Field>
                <Field label="N° de toque" hint="(facultatif)">
                  <TextInput value={toque} onChange={e => setToque(e.target.value)} placeholder="ex. C1234" />
                </Field>
                <Field label="Taille du cabinet" hint="(facultatif)">
                  <SelectInput value={effectif} onChange={e => setEffectif(e.target.value)} placeholder="Sélectionnez…" options={EFFECTIFS} />
                </Field>
              </div>
              <div style={{ marginTop: 20, fontSize: 12.5, color: K.ink2, background: K.cardSoft, border: `1px solid ${K.hair}`, borderRadius: 13, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.45 }}>
                <Ic.shieldLine style={{ width: 16, height: 16, color: K.bordeaux, flex: 'none', marginTop: 1 }} />
                <span><b style={{ color: K.ink, fontWeight: 600 }}>Confidentiel par construction.</b> Ces données ne servent qu'à votre cabinet, sont chiffrées et hébergées en Union européenne — jamais utilisées pour entraîner un modèle.</span>
              </div>
            </div>

            <ActionBar
              isMobile={isMobile} onBack={onBack} backLabel="Retour" backDisabled
              micro="Étape suivante : choisir vos domaines."
              nextLabel="Choisir mes domaines" onNext={onNext} nextDisabled={!canGoNext} />
          </section>
        )}

        {/* ───────── ÉTAPE 2 — Vos domaines ───────── */}
        {step === 1 && (
          <section key="p1" role="group" aria-label="Étape 2 — Vos domaines" style={{ display: 'flex', flexDirection: 'column', flex: 1, animation: 'cabRise .5s var(--cab-ease) both' }}>
            <ClaireHost kicker="Claire · Orchestratrice" isMobile={isMobile}>
              Dites-moi quelles matières vous traitez. <em style={{ fontStyle: 'italic', color: K.gildHi }}>Je convoque aussitôt</em> les bons spécialistes — votre équipe se forme sous vos yeux.
            </ClaireHost>

            <PanelHead stepNo="2"
              title={<>Choisissez <b style={{ fontWeight: 600, color: K.bordeaux }}>vos domaines</b>.</>}
              sub={<>Sélectionnez les matières de votre pratique. Chaque domaine <b style={{ color: K.ink, fontWeight: 600 }}>active le collaborateur spécialiste</b> correspondant. Vous pourrez toujours mobiliser les autres au besoin.</>} />

            {/* Compteur + visages activés */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: K.ink2, display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                <span style={{
                  fontFamily: F.monoCab, fontSize: 11, fontWeight: 600,
                  color: selected.length ? '#fff' : K.ink3,
                  background: selected.length ? 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))' : K.hair2,
                  padding: '3px 11px', borderRadius: 99,
                  boxShadow: selected.length ? '0 4px 12px -5px var(--cab-bordeaux-glow)' : 'none',
                }}>{selected.length}{selected.length > 1 ? ' domaines' : ' domaine'}</span>
                <span>sélectionné</span>
              </span>
              {specIds.length > 0 && (
                <span style={{ fontSize: 12.5, color: K.ink3, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <FaceStack ids={specIds} />
                  <span><b style={{ color: K.ink, fontWeight: 600 }}>{specIds.length}</b> spécialiste{specIds.length > 1 ? 's' : ''} activé{specIds.length > 1 ? 's' : ''}</span>
                </span>
              )}
            </div>

            {/* Grille des domaines — vraies données */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 14 }}>
              {DOMAINS.map(d => (
                <DomainCard key={d.id} domain={d} selected={selected.includes(d.id)} onToggle={toggleDom} isMobile={isMobile} />
              ))}
            </div>

            <ActionBar
              isMobile={isMobile} onBack={onBack} backLabel="Retour"
              micro="Sélectionnez au moins un domaine pour continuer."
              nextLabel="Rencontrer mon équipe" onNext={onNext} nextDisabled={!canGoNext} />
          </section>
        )}

        {/* ───────── ÉTAPE 3 — Rencontrez votre équipe ───────── */}
        {step === 2 && (
          <section key="p2" role="group" aria-label="Étape 3 — Votre équipe" style={{ display: 'flex', flexDirection: 'column', flex: 1, animation: 'cabRise .5s var(--cab-ease) both' }}>
            <ClaireHost kicker="Claire · Orchestratrice" isMobile={isMobile}>
              {newCount > 0 ? (
                <>J'ai convoqué <b style={{ fontWeight: 600, color: '#fff' }}>{newCount} spécialiste{newCount > 1 ? 's' : ''}</b> d'après vos domaines. Avec l'équipe de pilotage, vous êtes <em style={{ fontStyle: 'italic', color: K.gildHi }}>{rosterIds.length} à votre service</em>.</>
              ) : (
                <>Voici votre équipe. Chacun connaît déjà le contexte de vos dossiers — <em style={{ fontStyle: 'italic', color: K.gildHi }}>présentez-vous</em>, ils sont prêts à travailler.</>
              )}
            </ClaireHost>

            <PanelHead stepNo="3"
              title={<>Rencontrez <b style={{ fontWeight: 600, color: K.bordeaux }}>votre équipe</b>.</>}
              sub={<>Ces spécialistes ont rejoint votre cabinet d'après vos domaines. Ils sont disponibles 24/7 et se coordonnent via Claire.</>} />

            {/* Bandeau « d'office » + compteur */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
              <span style={{ fontSize: 12.5, color: K.ink3, display: 'inline-flex', alignItems: 'center', gap: 9, lineHeight: 1.4 }}>
                <span style={{ position: 'relative', width: 30, height: 30, flex: 'none' }}>
                  <img src={CLAIRE} alt="" style={{ width: '100%', height: '100%', borderRadius: 9, objectFit: 'cover', objectPosition: 'center top', border: `1px solid ${K.hair2}` }} />
                  <span aria-hidden="true" style={{ position: 'absolute', right: -3, bottom: -3, width: 11, height: 11, borderRadius: '50%', background: K.verified, border: `2px solid ${K.paper}` }} />
                </span>
                <span><b style={{ color: K.ink2, fontWeight: 600 }}>Claire</b>, <b style={{ color: K.ink2, fontWeight: 600 }}>Sophie</b> et l'accueil sont là d'office — ils pilotent et préparent pour tous vos dossiers.</span>
              </span>
              <span style={{ fontFamily: F.monoCab, fontSize: 11, fontWeight: 600, color: K.ink3, background: K.card, border: `1px solid ${K.hair}`, padding: '5px 12px', borderRadius: 99, boxShadow: K.lift1 }}>
                {rosterIds.length} collaborateurs dans votre cabinet
              </span>
            </div>

            {/* Roster — fiches « se présentent » (vraies données, entrée échelonnée) */}
            {rosterIds.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, alignItems: 'start' }}>
                {rosterIds.map((id, i) => {
                  const agent = agentById(id)
                  if (!agent) return null
                  return <SpecCard key={id} agent={agent} present={ALWAYS.includes(id)} index={i} />
                })}
              </div>
            ) : (
              <div style={{ borderRadius: K.rLg, border: `1.5px dashed ${K.hair3}`, background: K.cardSoft, padding: '40px 28px', textAlign: 'center', color: K.ink3 }}>
                <Ic.people style={{ width: 30, height: 30, color: K.ink4, margin: '0 auto 12px' }} />
                <b style={{ color: K.ink2, fontWeight: 600 }}>Aucun domaine sélectionné.</b><br />
                Revenez à l'étape précédente pour convoquer vos spécialistes.
              </div>
            )}

            <ActionBar
              isMobile={isMobile} onBack={() => goTo(1)} backLabel="Modifier mes domaines"
              micro="Vous pourrez ajouter d'autres spécialistes à tout moment."
              nextLabel="Ouvrir mon cabinet" onNext={onNext} nextDisabled={false} />
          </section>
        )}

        {/* ───────── ÉTAPE 4 — C'est prêt (cérémonial) ───────── */}
        {step === 3 && (
          <section key="p3" role="group" aria-label="Étape 4 — C'est prêt" style={{ display: 'flex', flexDirection: 'column', flex: 1, animation: 'cabRise .5s var(--cab-ease) both' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '10px 0' }}>
              {/* sceau */}
              <span aria-hidden="true" style={{ position: 'relative', width: 96, height: 96, marginBottom: 26 }}>
                <span style={{ position: 'absolute', inset: -12, borderRadius: 38, border: '1.5px solid var(--cab-bordeaux-hi)', opacity: 0, animation: 'cabRing 2.4s var(--cab-ease-soft) .4s infinite' }} />
                <span style={{ position: 'absolute', inset: 0, borderRadius: 28, display: 'grid', placeItems: 'center', background: 'linear-gradient(150deg, var(--cab-bordeaux-hi), var(--cab-bordeaux-deep))', color: '#fff', boxShadow: '0 22px 44px -18px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.3)' }}>
                  <svg viewBox="0 0 24 24" fill="none" style={{ width: 46, height: 46 }}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="#fff" strokeWidth="1.5" fill="rgba(255,255,255,.1)" /><path d="M8.5 12.2l2.3 2.3 4.5-4.8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
              </span>

              <h2 style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: 'clamp(30px, 4vw, 44px)', lineHeight: 1.08, letterSpacing: '-.02em', color: K.ink, maxWidth: '18ch', margin: 0 }}>
                <b style={{ fontWeight: 600, color: K.bordeaux }}>{cabinetName.trim() || 'Votre cabinet'}</b> est ouvert.
              </h2>
              <p style={{ fontSize: 15.5, color: K.ink2, marginTop: 15, maxWidth: '52ch', lineHeight: 1.55 }}>
                Votre équipe est en place et connaît déjà vos domaines. <b style={{ color: K.ink, fontWeight: 600 }}>Confiez-leur un premier dossier</b> — Claire le répartira au bon spécialiste.
              </p>

              {/* récap chips */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 24 }}>
                <span style={chipStyle}><Ic.home style={{ width: 14, height: 14, color: K.ink3 }} /><b style={{ color: K.ink, fontWeight: 600 }}>Barreau de {barreau || '—'}</b></span>
                <span style={chipStyle}><Ic.grid style={{ width: 14, height: 14, color: K.ink3 }} /><b style={{ color: K.ink, fontWeight: 600 }}>{selected.length}</b> domaines</span>
                <span style={chipStyle}><FaceStack ids={rosterIds} size={22} /><b style={{ color: K.ink, fontWeight: 600 }}>{rosterIds.length}</b> collaborateurs</span>
              </div>

              <button type="button" onClick={onEnter} disabled={saving}
                style={{
                  marginTop: 34, height: 52, padding: '0 30px', borderRadius: 14, fontFamily: F.sans, fontWeight: 600, fontSize: 15, color: '#fff',
                  background: 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))', display: 'inline-flex', alignItems: 'center', gap: 11,
                  boxShadow: '0 14px 30px -12px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.25)', border: 'none',
                  cursor: saving ? 'wait' : 'pointer', opacity: saving ? .7 : 1, transition: 'transform .2s, filter .2s',
                }}
                onMouseEnter={e => { if (!saving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.filter = 'brightness(1.05)' } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.filter = 'none' }}>
                <Ic.arrow style={{ width: 18, height: 18 }} />{saving ? 'Ouverture…' : 'Entrer dans Le Cabinet'}
              </button>
              <div style={{ marginTop: 16, fontSize: 12, color: K.ink4 }}>Vous arriverez sur Le Brief — l'accueil quotidien de votre cabinet.</div>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

const chipStyle = {
  fontSize: 12.5, color: K.ink2, background: K.card, border: `1px solid ${K.hair}`,
  padding: '8px 14px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 9, boxShadow: K.lift1,
}

/* ════════════════════════════════════════════════════════════════════════
   Barre d'action (bas de scène) — retour + micro-aide + bouton principal
   ════════════════════════════════════════════════════════════════════════ */
function ActionBar({ isMobile, onBack, backLabel, backDisabled, micro, nextLabel, onNext, nextDisabled }) {
  const [bHover, setBHover] = useState(false)
  const [nHover, setNHover] = useState(false)
  return (
    <div style={{
      marginTop: 'auto', paddingTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      ...(isMobile && { flexDirection: 'column-reverse', alignItems: 'stretch', gap: 12 }),
    }}>
      <button type="button" onClick={onBack} disabled={backDisabled}
        onMouseEnter={() => setBHover(true)} onMouseLeave={() => setBHover(false)}
        style={{
          fontFamily: F.sans, fontSize: 13.5, fontWeight: 600, color: bHover ? K.ink : K.ink2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 17px 11px 14px',
          borderRadius: 12, background: 'var(--cab-card)', border: `1px solid ${K.hair2}`,
          boxShadow: bHover ? K.lift2 : K.lift1, cursor: backDisabled ? 'default' : 'pointer',
          opacity: backDisabled ? 0 : 1, pointerEvents: backDisabled ? 'none' : 'auto',
          transform: bHover && !backDisabled ? 'translateX(-2px)' : 'none', transition: 'transform .2s, box-shadow .2s, color .2s',
        }}>
        <Ic.back style={{ width: 15, height: 15 }} />{backLabel}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, ...(isMobile && { flexDirection: 'column-reverse', alignItems: 'stretch' }) }}>
        {micro && <span style={{ fontSize: 12, color: K.ink4, maxWidth: isMobile ? 'none' : '30ch', textAlign: isMobile ? 'center' : 'right', lineHeight: 1.4 }}>{micro}</span>}
        <button type="button" onClick={onNext} disabled={nextDisabled}
          onMouseEnter={() => setNHover(true)} onMouseLeave={() => setNHover(false)}
          style={{
            height: 48, padding: '0 24px', borderRadius: 13, fontFamily: F.sans, fontWeight: 600, fontSize: 14.5,
            color: nextDisabled ? K.ink4 : '#fff',
            background: nextDisabled ? K.hair2 : 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, border: 'none',
            boxShadow: nextDisabled ? 'none' : '0 10px 24px -10px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.25)',
            cursor: nextDisabled ? 'not-allowed' : 'pointer',
            transform: nHover && !nextDisabled ? 'translateY(-1px)' : 'none', filter: nHover && !nextDisabled ? 'brightness(1.05)' : 'none',
            transition: 'transform .2s, filter .2s',
          }}>
          {nextLabel}<Ic.arrow style={{ width: 16, height: 16 }} />
        </button>
      </div>
    </div>
  )
}
